// src/pages/teacher/SightWordsWorksheetGenerator.tsx
import React, { useState } from 'react'
import { analytics } from '../../services/analytics-firebase'
import { ArrowLeft, Download, Loader2 } from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { generateSightWordsWorksheet } from '../../services/sightWordsWorksheetService'
import { generateMoodBasedPDF } from '../../services/moodBasedPDFGenerator'
import { useUserTier, useUsageLimit, useTrackGeneration } from '../../hooks/useUsageTracking'
import { WeeklyUsageCounter } from '../../components/worksheets/WeeklyUsageCounter'
import { UpgradeModal } from '../../components/worksheets/UpgradeModal'
import type { WorksheetData, MoodType } from '../../types/phonicsWorksheet'

const MOODS = [
  {
    id: 'overwhelmed' as const,
    emoji: '🌊',
    name: 'OVERWHELMED',
    description: '"My child is struggling today"',
    features: [
      '3 words (Tier 1 words only)',
      'Calm blue colors',
      '4 gentle activities',
      '"Do what feels right" message'
    ],
    colors: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      borderSelected: 'border-blue-600',
      bgSelected: 'bg-blue-100'
    }
  },
  {
    id: 'high_energy' as const,
    emoji: '⚡',
    name: 'HIGH ENERGY',
    description: '"My child is ready to work hard"',
    features: [
      '8 words (all tiers)',
      'Bright energizing colors',
      '4 active tasks',
      'Movement breaks included'
    ],
    colors: {
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      borderSelected: 'border-orange-600',
      bgSelected: 'bg-orange-100'
    }
  },
  {
    id: 'steady' as const,
    emoji: '🌱',
    name: 'STEADY',
    description: '"My child is in a good headspace"',
    features: [
      '5 words (tier 1 + tier 2 mix)',
      'Friendly purple tones',
      '4 balanced activities',
      'Standard worksheet format'
    ],
    colors: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      borderSelected: 'border-purple-600',
      bgSelected: 'bg-purple-100'
    }
  }
]

export const SightWordsWorksheetGenerator: React.FC = () => {
  const [selectedMood, setSelectedMood] = useState<MoodType | ''>('')
  const [isLoading, setIsLoading] = useState(false)
  const [worksheetData, setWorksheetData] = useState<WorksheetData | null>(null)

  const { isPremium } = useUserTier()
  const usageLimit = useUsageLimit()
  const { used, remaining, limit, canGenerate, resetDate, refetch, recheckCredits } = usageLimit
  const { trackGeneration } = useTrackGeneration()
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)

  // Track page view
  useEffect(() => {
    analytics.pageView('worksheet_generator_sight_words', {
      user_type: 'teacher',
      is_premium: isPremium
    })
  }, [isPremium])

  const generateWorksheet = async () => {
    if (!selectedMood) return
    
    // Check LIVE credits from Firestore
    if (!isPremium) {
      const currentCredits = await recheckCredits()
      const currentUsed = used || 0
      const freshCanGenerate = (currentUsed < 3) || currentCredits > 0
      
      if (!freshCanGenerate) {
        setShowUpgradeModal(true)
        analytics.upgradeModalShown('worksheet_limit_reached')
        return
      }
    }
  
    setIsLoading(true)
    try {
      // Track generation FIRST to prevent exploits
      if (!isPremium) {
        try {
          await trackGeneration()
        } catch (error) {
          setIsLoading(false)
          alert('Failed to track usage. Please try again.')
          return
        }
      }
      
      const result = await generateSightWordsWorksheet({ mood: selectedMood as MoodType })
      setWorksheetData(result)

      // Track generation
      analytics.worksheetGenerated('sight_words', selectedMood, isPremium)
      
      if (!isPremium) {
        await refetch()
      }
    } catch (error) {
      console.error('Error generating worksheet:', error)
      alert('Failed to generate worksheet. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleGenerateAnother = async () => {
    if (!selectedMood) return
    
    // Check LIVE credits from Firestore
    if (!isPremium) {
      const currentCredits = await recheckCredits()
      const currentUsed = used || 0
      const freshCanGenerate = (currentUsed < 3) || currentCredits > 0
      
      if (!freshCanGenerate) {
        setShowUpgradeModal(true)
        analytics.upgradeModalShown('worksheet_limit_reached')
        return
      }
    }
    
    setIsLoading(true)
    try {
      // Track generation FIRST
      if (!isPremium) {
        try {
          await trackGeneration()
        } catch (error) {
          setIsLoading(false)
          alert('Failed to track usage. Please try again.')
          return
        }
      }
      
      const result = await generateSightWordsWorksheet({ mood: selectedMood as MoodType })
      setWorksheetData(result)
      // Track regeneration
      analytics.worksheetGenerated('sight_words', selectedMood, isPremium)
      
      if (!isPremium) {
        await refetch()
      }
    } catch (error) {
      console.error('Error generating worksheet:', error)
      alert('Failed to generate worksheet. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDownloadPDF = async () => {
    if (!worksheetData) return
    
    try {
      const filename = `sight_words_${worksheetData.config.mood}_${Date.now()}.pdf`
      await generateMoodBasedPDF(worksheetData, filename)
      // Track download
      analytics.worksheetDownloaded('sight_words', selectedMood, isPremium)
    } catch (error) {
      console.error('Error downloading PDF:', error)
      alert('Failed to download PDF. Please try again.')
    }
  }

  const daysUntilReset = resetDate ? Math.max(1, Math.ceil((resetDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))) : 7

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => window.location.href = '/skill-builder'} 
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Sight Words Practice</h1>
              <p className="text-sm text-gray-600">Irregular words that must be memorized - cannot be sounded out</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Usage Counter */}
        {!isPremium && (
          <WeeklyUsageCounter
            used={used || 0}
            remaining={remaining || 0}
            limit={limit || 3}
            resetDate={resetDate || new Date()}
            isPremium={isPremium}
          />
        )}
        
        {/* Mood Selection */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">How's your child today?</h3>
          
          <div className="grid md:grid-cols-3 gap-4 items-stretch">
            {MOODS.map((mood) => (
              <button
                key={mood.id}
                onClick={() => setSelectedMood(mood.id)}
                className={`text-left p-6 rounded-lg border-2 transition-all h-full flex flex-col ${
                  selectedMood === mood.id
                    ? `${mood.colors.borderSelected} ${mood.colors.bgSelected}`
                    : `${mood.colors.border} ${mood.colors.bg} hover:${mood.colors.bgSelected}`
                }`}
              >
                <div className="text-4xl mb-2">{mood.emoji}</div>
                <h4 className="font-bold text-lg mb-1">{mood.name}</h4>
                <p className="text-sm text-gray-600 mb-3">{mood.description}</p>
                <ul className="text-xs text-gray-600 space-y-1">
                  {mood.features.map((feature, idx) => (
                    <li key={idx}>• {feature}</li>
                  ))}
                </ul>
              </button>
            ))}
          </div>
        </div>

        {/* Generate Button */}
        <div className="flex justify-center mb-8">
        {!worksheetData ? (
          <Button
            onClick={generateWorksheet}
            disabled={!selectedMood || isLoading}
            size="lg"
            className="min-w-[200px]"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Generating...
              </>
            ) : (
              'Generate Worksheet'
            )}
          </Button>
        ) : (
          <Button
            onClick={handleDownloadPDF}
            size="lg"
            className="min-w-[200px]"
          >
            <Download className="w-4 h-4 mr-2" />
            Download PDF
          </Button>
        )}
        </div>

        {/* Success State */}
        {worksheetData && (
          <div className="bg-white p-6 rounded-lg border border-green-200 shadow-sm">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-green-800 mb-2">✓ Worksheet Ready</h3>
              <p className="text-gray-600 mb-1">
                {worksheetData.wordCount} sight words • {worksheetData.estimatedTime} minutes
              </p>
              <p className="text-sm text-gray-500">
                Words: {worksheetData.words.join(', ')}
              </p>
            </div>

            <div className="space-y-3">
              <Button 
                onClick={handleDownloadPDF} 
                className="w-full"
                size="lg"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>

              {isPremium ? (
                <Button
                  onClick={handleGenerateAnother}
                  variant="outline"
                  className="w-full"
                  size="lg"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Generating...
                    </>
                  ) : (
                    'Generate Another'
                  )}
                </Button>
              ) : (
                <Button
                  disabled
                  variant="outline"
                  className="w-full opacity-50 cursor-not-allowed"
                  size="lg"
                >
                  Generate Another
                  <span className="ml-2 px-2 py-0.5 bg-yellow-400 text-yellow-900 text-xs rounded font-bold">
                    PREMIUM
                  </span>
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        daysUntilReset={daysUntilReset}
      />
    </div>
  )
}